import React, { useEffect, useState } from 'react';
import { Card, Button, Modal, Form, Input, Switch, Select, Space, Descriptions, App, Typography } from 'antd';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { scopes } from '../../../../authConfig';
import { getBookerLeagues, postBookerLeagues } from '../../../../services/admin-page-service';
import { BookerLeague } from '../../../../dto/admin-config-models';
import { SalesChannel } from '../../../../dto/model';

const { Option } = Select;
const { Title, Text } = Typography;

export const AdminBookerLeague: React.FC = () => {
  const { instance, inProgress } = useMsal();
  const { modal, notification } = App.useApp();
  const [data, setData] = useState<BookerLeague[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<BookerLeague | null>(null);
  const [form] = Form.useForm<BookerLeague>();

  const loadData = async () => {
    setLoading(true);
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const result = await getBookerLeagues(token.accessToken);
    if (result?.isSuccess && result.data) {
      setData(result.data);
    } else {
      notification.error({ message: 'Failed to load Booker Leagues' });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [instance, inProgress]);

  const openModal = (record?: BookerLeague) => {
    setEditing(record || null);
    form.setFieldsValue(
      record || {
        name: '',
        bookerIds: '',
        maxPriority: 1,
        isEnabled: true,
        salesChannel: SalesChannel.DigitalSales,
        isDeleted: false,
      }
    );
    setModalVisible(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    const payload: BookerLeague = {
      ...editing,
      ...values,
      isDeleted: false,
    };
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await postBookerLeagues(token.accessToken, [payload]);
    if (res.isSuccess) {
      notification.success({ message: editing ? 'Updated' : 'Created' });
      setModalVisible(false);
      loadData();
    } else {
      notification.error({ message: 'Save failed', description: res.errors?.join('\n') });
    }
  };

  const handleDeleteModal = async () => {
    if (!editing) return;
    const payload: BookerLeague = { ...editing, isDeleted: true };
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await postBookerLeagues(token.accessToken, [payload]);
    if (res.isSuccess) {
      notification.success({ message: 'Deleted' });
      setModalVisible(false);
      loadData();
    } else {
      notification.error({ message: 'Delete failed', description: res.errors?.join('\n') });
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          Booker Leagues
        </Title>
        <Text type="secondary">
          Create groups of bookers for each sales channel and set the maximum opportunity priority each group can receive. Add the relevant booker IDs as a comma-separated list; only enabled leagues apply.
        </Text>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button type="primary" onClick={() => openModal()}>
            + New Booker League
          </Button>
        </div>
      </div>

      <Space direction="vertical" style={{ width: '100%' }}>
        {data.map((league) => (
          <Card
            key={league.id}
            size="small"
            title={league.name}
            extra={
              <Button type="link" onClick={() => openModal(league)}>
                Edit
              </Button>
            }
            loading={loading}
          >
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Booker IDs">{league.bookerIds}</Descriptions.Item>
              <Descriptions.Item label="Max Priority">{league.maxPriority}</Descriptions.Item>
              <Descriptions.Item label="Enabled">{league.isEnabled ? 'Yes' : 'No'}</Descriptions.Item>
              <Descriptions.Item label="Sales Channel">{league.salesChannel}</Descriptions.Item>
            </Descriptions>
          </Card>
        ))}
        {data.length === 0 && !loading && <Card size="small">No Booker Leagues found.</Card>}
      </Space>

      <Modal
        title={editing ? 'Edit Booker League' : 'New Booker League'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button
            key="delete"
            danger
            disabled={!editing}
            onClick={() =>
              modal.confirm({
                title: 'Are you sure you want to delete this league?',
                okText: 'Yes',
                cancelText: 'No',
                onOk: handleDeleteModal,
              })
            }
          >
            Delete
          </Button>,
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleOk}>
            Save
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="bookerIds" label="Booker IDs (comma-separated)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="maxPriority" label="Max Priority" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item name="salesChannel" label="Sales Channel">
            <Select>
              {Object.values(SalesChannel).map((ch) => (
                <Option key={ch} value={ch}>
                  {ch}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="isEnabled" label="Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
